import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import EditColor from '../color/EditColor';
import ActionAreaCard from "../components/ActionAreaCard";
import ComboBox from "../components/ComboBox";
import CustomDialog from "../components/CustomDialog";
import ImageBox from "../components/ImageBox";
import RegionPicker from '../components/RegionPicker';
import useSnackbar from '../components/snackbar/useSnackBar';
import DataTable from '../data/DataTable';
import { useApi } from '../hooks/useApi';
import { useLanguage } from '../hooks/useLanguage';
import { useMeasurement } from '../hooks/useMeasurement';
import EditMaterial from '../material/EditMaterial';
import EditModel from '../model/EditModel';
import { API_URL, jsonBlob } from '../utils/lib';
import AssetOverview from "./AssetOverview";
import ProductAttributes from './ProductAttributes';
import ProductFiles from "./ProductFiles";
import ProductLink from './ProductLink';
import ProductObjects from './ProductObjects';

export default function Product({ userInfo }) {
  const { manufacturer_id, id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const version = searchParams.get("version");
  const versionSearchParam = `${version != null ? `version=${version}` : ''}`

  const [open, setOpen] = useState(false);
  const [openDataFileChooser, setOpenDataFileChooser] = useState(false);
  const [openPreviewPictureChooser, setOpenPreviewPictureChooser] = useState(false);
  const [openAssetViewer, setOpenAssetViewer] = useState({ id: null, type: null, fetchThroughProduct: null });
  const [manufacturer, setManufacturer] = useState({ id: undefined, name: "" });
  const [product, setProduct] = useState({
    name: "",
    description: "",
    productId: "",
    publicFlag: userInfo?.isPaying ? false : true,
    isFavorite: false
  });

  const location = useLocation();
  const { fromTab } = location.state || {};

  const isStatic = version != null || (id !== 'new' && !userInfo?.isAdmin && product.ownerId !== userInfo?.ownerId);

  const [assets, setAssets] = useState([]);
  // the initial fetched assets from the server
  const [fetchedAssets, setFetchedAssets] = useState([]);
  // set of assets that get selected by the user in the asset-overview
  const [diffAssets, setDiffAssets] = useState([]);
  const [previewPicture, setPreviewPicture] = useState({});
  const [deletePreviewPicture, setDeletePreviewPicture] = useState(false);

  // states for extra images / pdfs
  const [extraData, setExtraData] = useState([]);
  const [dataToRemove, setDataToRemove] = useState([]);

  // manage loading-state
  const [isLoading, setIsLoading] = useState(false);

  // object that holds reference to ProductAttributes-elements
  const attributesRef = useRef(null);
  // object that holds all attributes
  const [attributes, setAttributes] = useState([]);

  // list that holds all objects
  const [objects, setObjects] = useState([]);

  const requiredAttributes = useMemo(() => {
    let obj = {};
    objects.map(ob => ob.attributes.filter(at => at.required).forEach(at => obj[at.attribute.id] = true));
    return obj;
  }, [objects]);

  const valueRequiredAttributes = useMemo(() => {
    let obj = {};
    objects.map(ob => ob.attributes.filter(at => at.valueRequired).forEach(at => obj[at.attribute.id] = true));
    return obj;
  }, [objects]);

  const [linkElement, setLinkElement] = useState(null);

  // snackbar for information
  const { openSnackBar, closeSnackBar } = useSnackbar();

  const navigate = useNavigate();

  // locale for language
  const { locale } = useLanguage();
  const intl = useIntl();
  const api = useApi();

  // the current region
  const [region, setRegion] = useState('DE');

  const { setType } = useMeasurement();

  useEffect(() => {
    let active = true;
    if (id !== "new") {
      setIsLoading(true);
      api.get(`/product/${id}?lang=${locale}&${versionSearchParam}`)
        .then(response => {
          if (!active) return;
          parseFetchedData(response.data);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          if (!active) return;
          setIsLoading(false);
        });
    }

    if (manufacturer_id) {
      api.get(`/manufacturer/${manufacturer_id}`)
        .then(response => {
          if (!active) return;
          setManufacturer(response.data);
        })
        .catch(error => {
          console.log(error);
        });
    }

    return () => {
      // cancel state-setting-methods
      active = false;
    }
  }, [manufacturer_id, id, locale, versionSearchParam]);

  const handleClickOpen = () => {
    setDiffAssets([...assets]);
    setOpen(true);
  };

  const handleClose = (value) => {
    setDiffAssets([]);
    setOpen(false);
  };

  const handleAccept = () => {
    setAssets([...diffAssets]);
    setDiffAssets([]);
    setOpen(false);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setProduct({
      ...product,
      [name]: value,
    });
  };

  const removeData = (data) => {
    if (data.id >= 0 && !data.notUploaded) {
      // delete-request must be sent in next submit
      setDataToRemove(old => [...old, data]);
    }
    setExtraData(oldData => oldData.filter(d => d.id !== data.id));
  }


  const handleDataSelect = (data) => () => {
    setExtraData(oldData => {
      if (oldData.some(d => d.id === data.id)) {
        // if already selected, do not add data
        return [...oldData];
      } else {
        // if not already selected, add data
        data.notUploaded = true;
        return [...oldData, data];
      }
    });
    setOpenDataFileChooser(false);
  }

  /**
   * Parses fetched product-data into states.
   * @param {*} data 
   */
  const parseFetchedData = (data) => {
    setProduct(data);
    data.assets.forEach(asset => {
      if (asset.previewPicture) {
        asset.previewPictureId = asset.previewPicture.id;
      }
    })
    setAssets(data.assets);
    setFetchedAssets(data.assets);
    setManufacturer(data.manufacturer);
    setExtraData(data.data);
    setLinkElement(data.link);
    setRegion(data.country);
    if (data.country === 'US') {
      setType('imperial');
    } else {
      setType('metric');
    }
    // parse fetched attributes
    parseFetchedAttributes(data.attributes);
    setObjects(data.objects.map(ob => ({ ...ob })));
    if (data.previewPicture) {
      setPreviewPicture({ ...data.previewPicture, url: `${API_URL}/product/${id}/previewPicture?${versionSearchParam}`, file: undefined });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let attributeData = attributesRef.current ? attributesRef.current.getAllAttributeData() : [];
    if (!checkObjectAttributesConstraint(attributeData)) {
      return;
    }
    setIsLoading(true);
    const formData = new FormData();

    product.assets = assets;
    product.manufacturer = manufacturer;
    product.country = region;

    if (id === "new") {
      const productObj = { ...product };
      productObj.data = extraData;
      if (previewPicture.id) {
        productObj.previewPicture = previewPicture;
      } else {
        productObj.previewPicture = null;
      }
      productObj.link = linkElement;
      productObj.objects = getObjectFormData(objects);
      productObj.attributes = getAttributeFormData(attributeData);
      formData.append("product", jsonBlob(productObj));
      extraData.filter(data => data.id < 0).forEach((file) => {
        formData.append(`data`, file);
      });
      if (previewPicture.file) {
        formData.append("preview_picture", previewPicture.file);
      }
      api
        .post(`/product`, formData, {
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "multipart/form-data",
          }
        })
        .then((response) => {
          // handle the response
          navigate(`/products${fromTab != null ? `?tab=${fromTab}` : ''}`);
        })
        .catch((error) => {
          // handle errors
          console.log(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // put the current product
      // first delete all assets, that are in fetchedAssets, but not assets
      const dAssets = fetchedAssets.filter(a => assets.filter(asset => a.id === asset.id).length === 0);
      const assetDeletionPromise = dAssets.map(a => {
        return api
          .delete(`/product/${id}/asset/${a.id}`)
          .then(response => {
            // nothing should happen in case of response
          })
          .catch(error => {
            console.log(error);
          });
      });
      // now delete all extra files that are in dataToRemove
      const dataDeletionPromise = dataToRemove.map(data => {
        return api
          .delete(`/product/${id}/data/${data.id}`)
          .then(response => {
            // nothing should happen
          })
          .catch(error => {
            console.log(error);
          });
      });
      // get the attribute-form-data
      let attributeFormData = getAttributeFormData(attributeData);
      const previewPictureDeletionPromise = new Promise((resolve, reject) => {
        if (deletePreviewPicture) {
          api
            .delete(`/product/${id}/previewPicture`)
            .then(response => {
              setDeletePreviewPicture(false);
              resolve();
            })
            .catch(error => {
              console.log(error);
              reject();
            })
        } else {
          resolve();
        }
      });
      setDataToRemove([]);
      // now update the current product
      const productObj = { ...product };
      productObj.data = extraData;
      if (previewPicture.id) {
        productObj.previewPicture = previewPicture;
      } else {
        productObj.previewPicture = null;
      }
      productObj.link = linkElement;
      // does not need these properties (included as form-data)
      productObj.attributes = attributeFormData;
      productObj.objects = getObjectFormData(objects);
      delete productObj.assets;
      formData.append("product", jsonBlob(productObj));
      formData.append("assets", jsonBlob(product.assets));
      extraData.filter(data => data.id < 0).forEach((file) => {
        formData.append(`data`, file);
      });
      if (previewPicture.file) {
        formData.append("preview_picture", previewPicture.file);
      }
      // wait for all deletions to terminate
      const deletionPromise = Promise.all([...assetDeletionPromise, ...dataDeletionPromise, previewPictureDeletionPromise]);
      deletionPromise
        .then(() => {
          api
            .put(`/product/${id}`, formData, {
              headers: {
                Accept: "application/json, text/plain, */*",
                "Content-Type": "multipart/form-data"
              }
            })
            .then(response => {
              navigate(`/products${fromTab != null ? `?tab=${fromTab}` : ''}`);
            })
            .catch(error => {
              console.log(error);
            })
            .finally(() => {
              setIsLoading(false);
            });
        })
    }
  };

  const onSelectManufacturer = (manufacturer) => {
    setManufacturer(manufacturer);
  };

  /**
   * Adds the specified asset to the diff-assets.
   * @param {*} asset 
   */
  const onAdd = asset => {
    setDiffAssets(oldAssets => {
      if (oldAssets.filter(a => a.id === asset.id).length === 0) {
        // if not already part of product-assets, add
        if (asset.assetType === "MODEL") {
          // remove all other assets of type "MODEL"
          oldAssets = oldAssets.filter(a => a.assetType !== "MODEL");
        } else if (asset.assetType === "MATERIAL") {
          // remove all other assets of type "MATERIAL"
          oldAssets = oldAssets.filter(a => a.assetType !== "MATERIAL");
        }
        // set previewPictureId
        let previewPictureId = null;
        if (asset.previewPictureId) {
          previewPictureId = asset.previewPictureId;
        } else if (asset.previewPicture) {
          // this handles the case, that the asset-object has a different format
          previewPictureId = asset.previewPicture.id;
        }
        return [...oldAssets, { ...asset, previewPictureId, notUploaded: true }];
      } else {
        // already part of product-assets, no addition necessary
        return [...oldAssets];
      }
    });
  };

  /**
   * Removes the specified asset from the diff-assets.
   * @param {*} asset 
   */
  const onRemove = asset => {
    setDiffAssets(oldAssets => {
      if (oldAssets.filter(a => a.id === asset.id).length > 0) {
        // if already part of product-assets, remove
        return oldAssets.filter(a => a.id !== asset.id);
      } else {
        // not part of product-assets, no removal necessary
        return [...oldAssets];
      }
    });
  }

  /**
   * Removes the specified asset from the product.
   * @param {*} asset 
   */
  const removeAsset = asset => {
    setAssets(oldAssets => oldAssets.filter(a => a.id !== asset.id));
  };

  /**
   * Get the currently selected assets in the asset-overview.
   */
  const getSelectedAssets = useCallback(() => {
    return diffAssets;
  }, [diffAssets]);

  const handleSelectPreviewPicture = (data) => () => {
    data.notUploaded = true;
    data.url = `${API_URL}/data/${data.id}/file`
    setPreviewPicture(data);
    setOpenPreviewPictureChooser(false);
  }

  const handleDeletePreviewPicture = () => {
    if (previewPicture.notUploaded) {
      // must not delete from server
      setPreviewPicture({});
    } else {
      // delete in next submit
      setPreviewPicture({});
      setDeletePreviewPicture(true);
    }
  }

  /**
   * Reorders the objects by moving the object at position oldPos to newPos.
   * newPos may be -1 and then indicates that the object should be pushed to the end of the array.
   * @param {*} oldPos 
   * @param {*} newPos 
   */
  const reorderObjects = (oldPos, newPos) => {
    setObjects(old => {
      const [removedEl, _] = old.splice(oldPos, 1);
      if (newPos === -1) {
        old.push(removedEl);
      } else if (oldPos < newPos) {
        old.splice(newPos - 1, 0, removedEl);
      } else {
        old.splice(newPos, 0, removedEl);
      }
      return [...old];
    });
  }

  /**
   * Adds the specified object to the list of associated objects.
   * @param {*} ob 
   */
  const addObject = (ob) => {
    // add object
    setObjects(old => [...old, { ...ob }]);
    // add attributes from object to product
    if (attributesRef.current) {
      attributesRef.current.addAttributesFromObject(ob.attributes);
    }
  }

  /**
   * Removes the specified object from the list of associated objects.
   * @param {*} ob 
   * @returns 
   */
  const removeObject = (ob) => () => {
    setObjects(old => old.filter(o => o.id !== ob.id));
  }

  const handlePublicChange = (event) => {
    setProduct(oldProduct => ({ ...oldProduct, publicFlag: event.target.checked }));
  }

  const handleFavoriteChange = (event) => {
    if (isStatic && id) {
      // make immediate request to server
      api
        .put(`/product/${id}/favorite?value=${event.target.checked}`)
        .then(response => {
          setProduct(oldProduct => ({ ...oldProduct, isFavorite: response.data.isFavorite }));
        })
        .catch(error => {
          console.log(error);
        })
    } else {
      setProduct(oldProduct => ({ ...oldProduct, isFavorite: event.target.checked }));
    }
  }

  const parseFetchedAttributes = (attributes) => {
    setAttributes([...attributes])
  }

  /**
   * Check that all attributes of all objects have a corresponding attribute-instance.
   * @returns 
   */
  const checkObjectAttributesConstraint = (attributeData) => {
    let satisfied = true;
    const idObj = {};
    const addObj = {};
    attributeData.forEach(at => {
      if (at.attribute.id) {
        idObj[at.attribute.id] = true;
      }
    })
    objects.forEach(ob => {
      ob.attributes.forEach(at => {
        if (at.required && !idObj[at.attribute.id]) {
          satisfied = false;
          addObj[at.attribute.id] = at;
        }
      });
    });
    if (!satisfied) {
      if (attributesRef.current) {
        attributesRef.current.addAttributesFromObject(Object.values(addObj));
      }
      openSnackBar(intl.formatMessage({ id: "EditProduct.attributeObjectWarning" }));
    }
    return satisfied;
  }

  const getObjectFormData = (objectData) => {
    return objectData.map(ob => ({ id: ob.id }));
  }

  /**
   * Transforms the attributes that have been changed to objects that can be used in form-data.
   * @returns the form-data
   */
  const getAttributeFormData = (attributeData) => {
    const data = attributeData.map(attribute => {
      let at = { ...attribute, attribute: { ...attribute.attribute } };
      // if id was manually set, set id to null
      const atId = at.attribute.id;
      if (atId && atId < 0) {
        delete at.attribute.id;
      } else if (atId && atId > 0) {
        // id was not manually set, only need id of attribute
        delete at.attribute;
        at.attribute = { id: atId };
      }
      if (at.id && at.id < 0) {
        delete at.id;
      }
      return at;
    }).filter(at => at !== null);
    return data;
  }

  /**
   * Should be called when an asset is opened.
   * @param {*} assetId the asset-id
   * @param {*} type the asset-type
   * @param {*} fetchThroughProduct if the asset should be fetched through the product
   * @returns 
   */
  const handleOpenAsset = (assetId, type, fetchThroughProduct) => {
    switch (type) {
      case "model":
      case "material":
      case "color":
        setOpenAssetViewer({ id: assetId, type, fetchThroughProduct });
        return;
      default:
    }
  };

  /**
   * Should be called when an asset is closed.
   * @param {*} asset the (possible changed) asset
   */
  const handleCloseAsset = (asset) => {
    // close the asset-viewer
    setOpenAssetViewer({ id: null, type: null, fetchThroughProduct: null });
    // update the asset-data
    handleUpdateAsset(asset);
  }

  /**
   * Should be called when an asset is updated in order to update the product-asset-data.
   * @param {*} newAsset 
   */
  const handleUpdateAsset = (newAsset) => {
    setAssets(oldAssets => oldAssets.map(asset => {
      if (asset.id === newAsset.id) {
        let previewPictureId = null;
        if (newAsset.previewPicture) {
          previewPictureId = newAsset.previewPicture.id;
        }
        return {
          assetType: newAsset.assetType,
          id: newAsset.id,
          name: newAsset.name,
          object: newAsset.object,
          ownerId: newAsset.ownerId,
          previewPictureId,
          publicFlag: newAsset.publicFlag,
          notUploaded: true
        }
      } else {
        return { ...asset };
      }
    }));
  };

  /**
   * The overlay-dialog that displays the selected asset.
   */
  const assetDialog = useMemo(() => {
    switch (openAssetViewer.type) {
      case "model":
        return <EditModel userInfo={userInfo} id={openAssetViewer.id} pid={id} version={version} onClose={handleCloseAsset}
          fetchThroughProduct={openAssetViewer.fetchThroughProduct} viewStatic={false} />
      case "material":
        return <EditMaterial userInfo={userInfo} id={openAssetViewer.id} pid={id} version={version} onClose={handleCloseAsset}
          fetchThroughProduct={openAssetViewer.fetchThroughProduct} viewStatic={false} />;
      case "color":
        return <EditColor userInfo={userInfo} id={openAssetViewer.id} pid={id} version={version} onClose={handleCloseAsset}
          fetchThroughProduct={openAssetViewer.fetchThroughProduct} viewStatic={false} />;
      default:
        return;
    }
  }, [openAssetViewer, userInfo, id, handleCloseAsset]);

  /**
   * A list of all asset-elements as ActionAreaCard-components.
   */
  const assetElements = useMemo(() => {
    return assets.map((asset, idx) => {
      const assetType = asset.assetType.toLowerCase();
      let previewPictureURL = API_URL;
      // if the asset is already associated with the product on the server, fetch the preview-picture through the product
      if (!asset.notUploaded) {
        previewPictureURL += `/product/${id}`;
      }
      previewPictureURL += `/${assetType}/${asset.id}/previewPicture?quality=thumb&key=${asset.previewPictureId}`;
      if (version != null) {
        previewPictureURL += `&${versionSearchParam}`
      }
      let navigationLink = '';
      if (asset.notUploaded) {
        navigationLink += `/product/${id}`;
      }
      navigationLink = `/${assetType}/${asset.id}`;
      return (
        <Grid key={idx} item xs={12} sm={6} md={3}>
          <ActionAreaCard
            title={asset.name}
            description={asset.description}
            src={
              asset.previewPictureId &&
              previewPictureURL
            }
            color={asset.hex && asset.hex}
            handleClick={() => {
              // navigate(navigationLink);
              handleOpenAsset(asset.id, assetType, asset.notUploaded ? false : true);
            }
            }
            onDelete={() => removeAsset(asset)}
            disabled={isStatic}
          />
        </Grid>
      );
    })
  }, [assets, id, isStatic, versionSearchParam]);

  const textFieldStyle = {
    width: '90%'
  }

  return (
    <Container maxWidth='xl' >
      {isLoading && <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          position: "fixed",
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: "center",
          alignItems: "center",
          zIndex: 10
        }}
      >
        <CircularProgress color="primary" />
      </Box>}
      <Typography variant="h4" gutterBottom sx={{ marginTop: 2 }}>
        <FormattedMessage id="EditProduct.heading" />
      </Typography>
      <CustomDialog
        title={intl.formatMessage({ id: "EditProduct.addPreviewPicture" })}
        content={<DataTable selectable={true} usageFlags={{ image: true }} onSelect={handleSelectPreviewPicture} />}
        isOpen={openPreviewPictureChooser}
        handleAccept={() => setOpenPreviewPictureChooser(false)}
        handleClose={() => setOpenPreviewPictureChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditProduct.chooseAssets" })}
        content={<AssetOverview productId={id} onAdd={onAdd} onRemove={onRemove} getSelectedAssets={getSelectedAssets} />}
        isOpen={open}
        handleAccept={handleAccept}
        handleClose={handleClose}
        maxWidth={"xl"}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditProduct.addPicturePdf" })}
        content={<DataTable selectable={true} usageFlags={{ svg: true, image: true, pdf: true }} onSelect={handleDataSelect} />}
        isOpen={openDataFileChooser}
        handleAccept={() => setOpenDataFileChooser(false)}
        handleClose={() => setOpenDataFileChooser(false)}
        maxWidth={"xl"}
        hideConfirmation={true}
      />
      <CustomDialog
        title={intl.formatMessage({ id: "EditProduct.asset" })}
        content={assetDialog}
        isOpen={openAssetViewer.id !== null}
        handleAccept={() => { }}
        handleClose={handleCloseAsset}
        maxWidth={"xl"}
        hideConfirmation={true}
        displayClose={true}
      />
      <Box
        component="form"
        onSubmit={handleSubmit}
        marginTop={4}
      >
        <Box
          width='100%'
          display='flex'
          flexDirection='column'
          justifyContent='center'
          alignItems='center'
          sx={{ paddingBottom: 2, marginBottom: 2 }}
        >
          <Grid container flexDirection='column' justifyContent='center' alignItems='center' item xs={12} md={6} >
            {previewPicture.url && <ImageBox url={previewPicture.url} uploaded={previewPicture.notUploaded !== true} onDelete={handleDeletePreviewPicture} disabled={isStatic} />}
            {!isStatic && <Button variant="outlined" sx={{ marginTop: 1 }} onClick={() => setOpenPreviewPictureChooser(true)} ><FormattedMessage id="EditProduct.addPreviewPicture" /></Button>}
          </Grid>
        </Box>
        <Grid container spacing={3} alignItems="center" sx={{ border: '1px dashed black', borderRadius: 3, width: "100%", paddingBottom: 2, marginBottom: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              required
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              name={"name"}
              label={intl.formatMessage({ id: "EditProduct.name" })}
              type="text"
              value={product.name}
              sx={textFieldStyle}
              onChange={(event) => {
                handleInputChange(event);
              }}
              inputProps={{ maxLength: 50 }}
              disabled={isStatic}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              name={"productId"}
              label={intl.formatMessage({ id: "EditProduct.articleNumber" })}
              type="text"
              value={product.productId}
              sx={textFieldStyle}
              onChange={(event) => {
                handleInputChange(event);
              }}
              disabled={isStatic}
              inputProps={{ maxLength: 50 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ComboBox
              label={intl.formatMessage({ id: "EditProduct.manufacturer" })}
              selection={manufacturer}
              onSelect={onSelectManufacturer}
              required={false}
              url={`/manufacturer/all`}
              style={textFieldStyle}
              idTag={"manufacturer"}
              disabled={isStatic}
            />
          </Grid>

          <Grid item xs={12} sm={12} md={6}>
            <TextField
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              size="small"
              name={"description"}
              label={intl.formatMessage({ id: "EditProduct.description" })}
              type="text"
              multiline
              value={product.description}
              sx={{
                width: {
                  xs: '90%',
                  sm: '95%',
                  md: '95%'
                }
              }}
              onChange={(event) => {
                handleInputChange(event);
              }}
              disabled={isStatic}
              inputProps={{ maxLength: 255 }}
            />
          </Grid>

          <ProductObjects isStatic={isStatic} objects={objects} addObjectToProduct={addObject} removeObject={removeObject} reorderObjects={reorderObjects} />

          <Grid item xs={12}>
            <ProductLink link={linkElement} setLink={setLinkElement} isStatic={isStatic} />
          </Grid>

          {/* only paying users can make product public */}
          {!isStatic && userInfo?.isPaying && <Grid item xs={12} sm={12} md={12}>
            <FormControlLabel control={<Checkbox checked={product.publicFlag} onChange={handlePublicChange} />} label={intl.formatMessage({ id: "Edit.published" })} />
          </Grid>}

          <Grid item xs={12} sm={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox icon={<StarBorderIcon />} checkedIcon={<StarIcon />} checked={product.isFavorite} onChange={handleFavoriteChange} />
              }
              label={
                product.isFavorite ? intl.formatMessage({ id: "EditProduct.removeFromFavorites" }) : intl.formatMessage({ id: "EditProduct.addToFavorites" })
              } />
          </Grid>
          <Grid item xs={12}>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center' }} >
              <Typography variant="h6" >
                <FormattedMessage id="EditAttribute.region" />
              </Typography>
              <RegionPicker isStatic={isStatic} region={region} setRegion={(v) => {
                if (v === 'US') {
                  setType('imperial');
                } else {
                  setType('metric');
                }
                setRegion(v);
              }} />
            </div>
          </Grid>
          <ProductAttributes userInfo={userInfo} isStatic={isStatic} initialAttributes={attributes} ref={attributesRef} region={region} requiredAttributes={requiredAttributes} valueRequiredAttributes={valueRequiredAttributes} />
        </Grid>


        <Grid container spacing={3} marginBottom={3}>
          {!isStatic && <Grid item xs={12}>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleClickOpen}
            >
              <FormattedMessage id="EditProduct.addAsset" />
            </Button>
          </Grid>}
          {assetElements}
        </Grid>

        <Grid container spacing={3}>
          {!isStatic && <Grid item xs={12}>
            <Button variant="outlined" onClick={() => {
              // fileRef.current?.click()
              setOpenDataFileChooser(true);
            }}><FormattedMessage id="EditProduct.addPicturePdf" /></Button>
          </Grid>}
          <Grid item xs={12}>
            <ProductFiles removeData={removeData} extraData={extraData} disabled={isStatic} id={id} version={version} />
          </Grid>
        </Grid>

        {!isStatic && <Button sx={{ marginTop: 2, marginBottom: 2 }} variant="contained" type="submit">
          <FormattedMessage id="Edit.save" />
        </Button>}
      </Box>
    </Container>
  );
}
